import { createElement, useEffect, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useTypedDispatch } from '@app/store/hooks/useTypedDispatch';
import { useGetPolicySettingsQuery } from '@entities/policy';
import {
  IConfigIssue,
  IEditorChanges,
  IEditorPolicy,
  IEditorPolicyInput,
  IEditorRuleInput,
  IEditorSession,
  IRoutingConfigVersion,
  ISaveDraftInput,
  nextAvailableName,
  routingConfigEditorActions,
  toEditorPolicies,
  useEditorChanges,
  useEditorSession,
  useRestoreVersion,
  useSaveDraft,
  useStaleDroppedVersionId,
} from '@entities/routing/config-version';
import { useModal } from '@shared/components/Modal';

import EditVersionMetadataModal from './components/EditVersionMetadataModal';
import SaveDraftModal from './components/SaveDraftModal';

interface IParams {
  // The version on screen; undefined while the page is still loading.
  version: IRoutingConfigVersion | undefined;
  // null when the URL has no valid ?version and the active config is shown implicitly.
  versionId: number | null;
  // Every fetched version — supplies the names Save Draft checks against.
  versions: IRoutingConfigVersion[];
}

export interface IConfigEditor {
  isEditing: boolean;
  changes: IEditorChanges;
  session: IEditorSession | null;
  serverIssues: IConfigIssue[];
  isSaving: boolean;
  // Restore's draft request is in flight.
  isRestoring: boolean;
  // View mode: the loaded version's policies; edit mode: the working copy — same shape.
  policies: IEditorPolicy[];
  onEdit: () => void;
  onDiscard: () => void;
  onSave: () => void;
  // Archived version → a new draft cloned from it, opened for editing.
  onRestore: () => Promise<void>;
  // Opens the name & comment editor (any status).
  onEditDetails: () => void;
  onTogglePolicy: (uid: string) => void;
  // Policy edits — working copy only, no requests.
  onAddPolicy: (input: IEditorPolicyInput) => void;
  onUpdatePolicy: (uid: string, input: IEditorPolicyInput) => void;
  onClonePolicy: (uid: string) => void;
  onRemovePolicy: (uid: string) => void;
  onMovePolicy: (uid: string, toIndex: number) => void;
  // Rule edits — same working copy, same rules.
  onMoveRule: (policyUid: string, ruleUid: string, toIndex: number) => void;
  onAddRule: (policyUid: string, input: IEditorRuleInput) => void;
  onUpdateRule: (policyUid: string, ruleUid: string, input: IEditorRuleInput) => void;
  onRemoveRule: (policyUid: string, ruleUid: string) => void;
  onToggleRule: (policyUid: string, ruleUid: string) => void;
}

// The edit-session orchestration for the config view: hydration, the button
// handlers, and the two-step Save Draft. Keeps the widget as view composition.
export const useConfigEditor = ({ version, versionId, versions }: IParams): IConfigEditor => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Plain dispatch (not useActions): keeps the hydrate effect's deps stable.
  const dispatch = useTypedDispatch();
  const { openModal } = useModal();
  const session = useEditorSession();
  const changes = useEditorChanges();
  const staleDroppedVersionId = useStaleDroppedVersionId();
  // Server issues from the last Save Draft, until the next edit or attempt.
  const [serverIssues, setServerIssues] = useState<IConfigIssue[]>([]);
  const isEditing = session !== null && version != null && session.versionId === version.id;

  // Prefetch the dictionaries while editing so the form opens without a request.
  useGetPolicySettingsQuery(undefined, { skip: !isEditing });

  // Restore or drop this version's session on each (re)load; keyed on id + updated_at
  // so refetches never reset a live session.
  const loadedVersionId = version?.id;
  const loadedUpdatedAt = version?.updated_at;
  useEffect(() => {
    if (loadedVersionId === undefined) return;
    dispatch(
      routingConfigEditorActions.hydrateEditor({
        versionId: loadedVersionId,
        updatedAt: loadedUpdatedAt,
      }),
    );
  }, [dispatch, loadedVersionId, loadedUpdatedAt]);

  useEffect(() => {
    if (staleDroppedVersionId === null) return;
    toast.info(
      `v${staleDroppedVersionId} changed on the server since your unsaved edits — they were discarded.`,
    );
    dispatch(routingConfigEditorActions.acknowledgeStaleDrop());
  }, [dispatch, staleDroppedVersionId]);

  const showVersion = (id: number, replace = false) => {
    const next = new URLSearchParams(searchParams);
    next.set('version', String(id));
    setSearchParams(next, { replace });
  };

  // Move the URL onto the new draft before the PUT, so a failed PUT retries there.
  const { saveDraft, isCreatingDraft, isSaving } = useSaveDraft({
    onDraftCreated: draft => showVersion(draft.id),
  });
  const { restoreVersion, isRestoring } = useRestoreVersion();

  const runSave = async (input: ISaveDraftInput) => {
    setServerIssues([]);
    const result = await saveDraft(input);

    if (result.status === 'saved') {
      dispatch(routingConfigEditorActions.finishEditing());
      toast.success(`Draft v${result.version.id} saved.`);
    } else if (result.stage === 'update') {
      setServerIssues(result.issues);
    }

    return result;
  };

  const onSave = () => {
    if (!version) return;
    // A clone leaves the base in place, so the base name stays taken; a draft saved
    // in place is only compared against the other versions.
    const takenNames = versions
      .filter(item => isCreatingDraft || item.id !== version.id)
      .map(item => item.name);

    // createElement, not JSX: this is a .ts hook, kept out of the .tsx view layer.
    openModal(
      createElement(SaveDraftModal, {
        baseVersionId: version.id,
        // A clone would repeat the base name, so it opens on the first free "(N)";
        // a draft saved in place keeps its own.
        baseName: isCreatingDraft ? nextAvailableName(version.name, takenNames) : version.name,
        baseComment: isCreatingDraft ? null : version.comment,
        isCreatingDraft,
        takenNames,
        onSubmit: runSave,
      }),
      'sm',
    );
  };

  const onEdit = () => {
    if (!version) return;
    dispatch(routingConfigEditorActions.startEditing(version));
    // Pin the edited version in the URL, so a reload after someone else publishes
    // stays on this session instead of following the new active version.
    if (versionId === null) showVersion(version.id, true);
  };

  // Restore: the hook clones the version into a draft and opens the session on it;
  // the URL follows, so the page lands on the draft in edit mode.
  const onRestore = async () => {
    if (!version) return;
    const result = await restoreVersion(
      version,
      versions.map(item => item.name),
    );

    if (result.status === 'failed') {
      toast.error(result.issues[0].message);
      return;
    }

    showVersion(result.draft.id);
    toast.success(
      `Draft v${result.draft.id} created from v${version.id} — you are editing it now.`,
    );
  };

  // The version's own name is not a collision.
  const onEditDetails = () => {
    if (!version) return;
    openModal(
      createElement(EditVersionMetadataModal, {
        version,
        takenNames: versions.filter(item => item.id !== version.id).map(item => item.name),
      }),
      'sm',
    );
  };

  // Every local edit invalidates the path-keyed issues of the last save attempt.
  const edit = (action: Parameters<typeof dispatch>[0]) => {
    setServerIssues([]);
    dispatch(action);
  };

  const onDiscard = () => edit(routingConfigEditorActions.discardEditing());
  const onTogglePolicy = (uid: string) => edit(routingConfigEditorActions.togglePolicyActive(uid));
  const onAddPolicy = (input: IEditorPolicyInput) => {
    if (isEditing) edit(routingConfigEditorActions.addPolicy(input));
  };
  const onUpdatePolicy = (uid: string, input: IEditorPolicyInput) =>
    edit(routingConfigEditorActions.updatePolicy({ uid, input }));
  const onClonePolicy = (uid: string) => edit(routingConfigEditorActions.clonePolicy(uid));
  const onRemovePolicy = (uid: string) => edit(routingConfigEditorActions.removePolicy(uid));
  const onMovePolicy = (uid: string, toIndex: number) =>
    edit(routingConfigEditorActions.movePolicy({ uid, toIndex }));
  const onMoveRule = (policyUid: string, ruleUid: string, toIndex: number) =>
    edit(routingConfigEditorActions.moveRule({ policyUid, ruleUid, toIndex }));
  const onAddRule = (policyUid: string, input: IEditorRuleInput) =>
    edit(routingConfigEditorActions.addRule({ policyUid, input }));
  const onUpdateRule = (policyUid: string, ruleUid: string, input: IEditorRuleInput) =>
    edit(routingConfigEditorActions.updateRule({ policyUid, ruleUid, input }));
  const onRemoveRule = (policyUid: string, ruleUid: string) =>
    edit(routingConfigEditorActions.removeRule({ policyUid, ruleUid }));
  const onToggleRule = (policyUid: string, ruleUid: string) =>
    edit(routingConfigEditorActions.toggleRuleActive({ policyUid, ruleUid }));

  const policies = useMemo(
    () => (isEditing ? session.draft : toEditorPolicies(version?.policies ?? [])),
    [isEditing, session, version],
  );

  return {
    isEditing,
    changes,
    session,
    serverIssues,
    isSaving,
    isRestoring,
    policies,
    onEdit,
    onDiscard,
    onSave,
    onRestore,
    onEditDetails,
    onTogglePolicy,
    onAddPolicy,
    onUpdatePolicy,
    onClonePolicy,
    onRemovePolicy,
    onMovePolicy,
    onMoveRule,
    onAddRule,
    onUpdateRule,
    onRemoveRule,
    onToggleRule,
  };
};
